<template>
  <div class="cfg-tab">
    <div class="cfg-intro">
      <v-icon>mdi-information-outline</v-icon>
      <div>
        <p>
          Sălile (bloc, etaj, sală) în care stau echipamentele și unde se livrează cele noi. Demersurile depuse își
          păstrează sala cu care au fost depuse.
        </p>
        <p>O sală ascunsă nu mai poate fi aleasă pentru nimic nou; echipamentele din ea rămân.</p>
      </div>
    </div>

    <div class="cfg-toolbar">
      <h3 class="panel-title">Locații <span class="count">{{ filtered.length }}</span></h3>
      <span class="grow-spacer" />
      <v-text-field
        v-model="search"
        class="cfg-search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Caută bloc, etaj sau sală"
        aria-label="Caută bloc, etaj sau sală"
        outlined
        dense
        single-line
        hide-details
        clearable
      ></v-text-field>
      <v-btn class="btn-green" @click="openCreate()"><v-icon left>mdi-plus</v-icon>Sală nouă</v-btn>
    </div>

    <div v-if="!searching && groups.length > 1" class="locs__tree-btns">
      <v-btn text small @click="setAll(true)"><v-icon left small>mdi-unfold-more-horizontal</v-icon>Extinde tot</v-btn>
      <v-btn text small @click="setAll(false)"><v-icon left small>mdi-unfold-less-horizontal</v-icon>Restrânge tot</v-btn>
    </div>

    <v-progress-linear v-if="loading && !loaded" indeterminate color="primary" class="mb-2" />
    <div v-if="loaded && !groups.length" class="cfg-list">
      <div class="cfg-empty">{{ searching ? "Nicio sală găsită." : "Nicio locație." }}</div>
    </div>

    <div v-for="g in groups" :key="g.block" class="locs__group">
      <button type="button" class="locs__head" :aria-expanded="String(isOpen(g.block))" @click="toggleBlock(g.block)">
        <v-icon class="locs__chevron">{{ isOpen(g.block) ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
        <v-icon class="locs__building">mdi-office-building-outline</v-icon>
        <span class="locs__block">{{ g.block }}</span>
        <span class="locs__meta">{{ count(g.rooms, "sală", "săli") }} · {{ count(g.equipment, "echipament", "echipamente") }}</span>
      </button>
      <div v-if="isOpen(g.block)" class="cfg-list locs__list">
        <template v-for="f in g.floors">
          <div :key="`f-${f.floor}`" class="locs__floor">
            <v-icon small>mdi-stairs</v-icon>{{ f.floor }}
            <v-spacer></v-spacer>
            <v-btn text small color="primary" @click="openCreate(g.block, f.floor)">
              <v-icon left small>mdi-plus</v-icon>Sală aici
            </v-btn>
          </div>
          <div v-for="l in f.rows" :key="l.id" class="cfg-row locs__row" :class="{ 'is-hidden': l.is_hidden }">
            <div class="cfg-row__main">
              <div class="cfg-row__title">
                <span class="cfg-row__name">{{ l.room }}</span>
                <span v-if="l.is_hidden" class="cfg-tag"><v-icon>mdi-eye-off-outline</v-icon>Ascunsă</span>
              </div>
              <div class="cfg-usage">{{ usageText(l.usage) || "Fără echipamente" }}</div>
            </div>
            <div class="cfg-row__actions">
              <v-btn icon color="primary" aria-label="Editează sala" @click="openEdit(l)">
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn
                icon
                :aria-label="l.is_hidden ? 'Arată sala' : 'Ascunde sala'"
                :title="l.is_hidden ? 'Arată din nou' : 'Ascunde'"
                :disabled="saving"
                @click="toggleHidden(l)"
              >
                <v-icon>{{ l.is_hidden ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
              </v-btn>
              <v-btn icon color="error" aria-label="Șterge sala" @click="remove(l)">
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </template>
      </div>
    </div>

    <v-dialog v-model="dialog" persistent scrollable max-width="640" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card>
        <v-form class="fdlg__form" @submit.prevent="save">
          <div class="fdlg__head">
            <div>
              <h2 class="fdlg__title">{{ editing ? "Editează locația" : "Sală nouă" }}</h2>
              <p class="fdlg__sub">{{ editing ? locationText(editing) : "Alegeți un bloc și un etaj existente sau scrieți altele noi." }}</p>
            </div>
            <v-btn icon aria-label="Închide" @click="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text ref="body" class="fdlg__body">
            <div v-if="editing && editing.used" class="cfg-used-note">
              <v-icon small>mdi-alert-outline</v-icon>
              <p>
                În uz: {{ usageText(editing.usage) }}. Denumirea nouă se vede pe echipamente; demersurile depuse
                păstrează sala cu care au fost depuse.
              </p>
            </div>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-map-marker-outline</v-icon>Locația</div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-combobox
                    v-model="form.block"
                    :search-input.sync="blockTyped"
                    :items="blockOptions"
                    label="Bloc*"
                    prepend-inner-icon="mdi-office-building-outline"
                    outlined
                    dense
                    :maxlength="30"
                    :error-messages="formError && !pick(form.block, blockTyped) ? formError : ''"
                  ></v-combobox>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-combobox
                    v-model="form.floor"
                    :search-input.sync="floorTyped"
                    :items="floorOptions"
                    label="Etaj*"
                    prepend-inner-icon="mdi-stairs"
                    outlined
                    dense
                    :maxlength="20"
                    :error-messages="formError && !pick(form.floor, floorTyped) ? formError : ''"
                  ></v-combobox>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.room"
                    label="Sală / aulă*"
                    prepend-inner-icon="mdi-door"
                    placeholder="de exemplu: Aula 204"
                    outlined
                    dense
                    maxlength="70"
                    :error-messages="formError && !form.room.trim() ? formError : ''"
                  ></v-text-field>
                </v-col>
              </v-row>
            </section>
          </v-card-text>

          <div class="fdlg__foot">
            <v-spacer></v-spacer>
            <v-btn text large @click="dialog = false">Anulare</v-btn>
            <v-btn class="btn-green" large type="submit" :loading="saving">
              <v-icon left>mdi-content-save-outline</v-icon>Salvează
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <ConfirmUsedDialog ref="confirmUsed" />
    <RemoveDialog ref="remove" />
  </div>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import configMixin from "./configMixin";
import ConfirmUsedDialog from "./ConfirmUsedDialog.vue";
import RemoveDialog from "./RemoveDialog.vue";
import { cleanText, count, plain, usageText } from "./format";

const locationText = (l) => [l.block, l.floor, l.room].filter(Boolean).join(", ");
const natural = (a, b) => String(a).localeCompare(String(b), "ro", { numeric: true });

export default {
  name: "LocationsTab",
  components: { ConfirmUsedDialog, RemoveDialog },
  mixins: [configMixin],
  data() {
    return {
      locations: [],
      search: "",
      // block -> true when its rooms are shown
      openBlocks: {},
      dialog: false,
      editing: null,
      form: { block: "", floor: "", room: "" },
      // what is typed in the comboboxes before it is committed (blur / enter)
      blockTyped: null,
      floorTyped: null,
      formError: "",
    };
  },
  computed: {
    searching() {
      return !!plain(this.search);
    },
    filtered() {
      const term = plain(this.search);
      return term ? this.locations.filter((l) => plain(locationText(l)).includes(term)) : this.locations;
    },
    // rooms by block, then floor (the server already sorts them naturally)
    groups() {
      const groups = [];
      const byBlock = new Map();
      this.filtered.forEach((l) => {
        let g = byBlock.get(l.block);
        if (!g) {
          g = { block: l.block, rooms: 0, equipment: 0, floors: [], byFloor: new Map() };
          byBlock.set(l.block, g);
          groups.push(g);
        }
        let f = g.byFloor.get(l.floor);
        if (!f) {
          f = { floor: l.floor, rows: [] };
          g.byFloor.set(l.floor, f);
          g.floors.push(f);
        }
        f.rows.push(l);
        g.rooms++;
        g.equipment += Number((l.usage && l.usage.equipment) || 0);
      });
      return groups;
    },
    blockOptions() {
      return [...new Set(this.locations.map((l) => l.block).filter(Boolean))].sort(natural);
    },
    floorOptions() {
      const block = plain(this.pick(this.form.block, this.blockTyped));
      const rows = block ? this.locations.filter((l) => plain(l.block) === block) : this.locations;
      return [...new Set((rows.length ? rows : this.locations).map((l) => l.floor).filter(Boolean))].sort(natural);
    },
  },
  methods: {
    usageText,
    count,
    locationText,
    async load() {
      this.locations = await api.getLocations();
    },
    // a combobox value is committed on blur; take the typed text when there is one
    pick(value, typed) {
      return cleanText(typed != null && String(typed).trim() !== "" ? typed : value);
    },
    isOpen(block) {
      return this.searching || !!this.openBlocks[block];
    },
    toggleBlock(block) {
      if (this.searching) return;
      this.$set(this.openBlocks, block, !this.openBlocks[block]);
    },
    setAll(open) {
      const all = {};
      if (open) this.groups.forEach((g) => (all[g.block] = true));
      this.openBlocks = all;
    },
    openCreate(block = "", floor = "") {
      this.editing = null;
      this.form = { block, floor, room: "" };
      this.openDialog();
    },
    openEdit(l) {
      this.editing = l;
      this.form = { block: l.block, floor: l.floor, room: l.room };
      this.openDialog();
    },
    openDialog() {
      this.blockTyped = null;
      this.floorTyped = null;
      this.formError = "";
      this.dialog = true;
      helper.scrollToTop(this);
    },
    async save() {
      const values = {
        block: this.pick(this.form.block, this.blockTyped),
        floor: this.pick(this.form.floor, this.floorTyped),
        room: cleanText(this.form.room),
      };
      if (!values.block || !values.floor || !values.room) {
        this.formError = "Obligatoriu";
        return;
      }
      const l = this.editing;
      if (!l) {
        const created = await this.change(() => api.createLocation(values), {
          done: `Sala „${locationText(values)}” a fost adăugată`,
          fallback: "Eroare la adăugarea locației",
        });
        if (created) {
          this.dialog = false;
          this.$set(this.openBlocks, values.block, true);
        }
        return;
      }
      const body = {};
      ["block", "floor", "room"].forEach((field) => {
        if (values[field] !== l[field]) body[field] = values[field];
      });
      if (!Object.keys(body).length) {
        this.dialog = false;
        return;
      }
      const saved = await this.change((extra) => api.updateLocation(l.id, { ...body, ...extra }), {
        done: `Locația a fost salvată: ${locationText(values)}`,
        fallback: "Eroare la salvarea locației",
        confirm: {
          title: `Modificați sala „${l.room}”?`,
          from: locationText(l),
          to: locationText(values),
          changes: ["Echipamentele din această sală arată locația nouă."],
          keeps: ["Demersurile depuse păstrează sala cu care au fost depuse: sunt documente."],
        },
      });
      if (saved) {
        this.dialog = false;
        this.$set(this.openBlocks, values.block, true);
      }
    },
    toggleHidden(l) {
      const hide = !l.is_hidden;
      return this.change(() => api.updateLocation(l.id, { is_hidden: hide }), {
        done: hide ? `Sala „${l.room}” este ascunsă: nu mai poate fi aleasă pentru nimic nou` : `Sala „${l.room}” poate fi aleasă din nou`,
        fallback: "Eroare la salvarea locației",
      });
    },
    async remove(l) {
      const canDelete = !l.used;
      const choice = await this.$refs.remove.ask({
        title: canDelete ? `Ștergeți sala „${locationText(l)}”?` : `Sala „${l.room}” nu poate fi ștearsă`,
        text: canDelete
          ? "Nu are echipamente, deci poate fi ștearsă."
          : `Are ${usageText(l.usage)} (și istoric). ` +
            (l.is_hidden
              ? "Este deja ascunsă, deci nu mai poate fi aleasă pentru nimic nou."
              : "Ascundeți-o: nu va mai putea fi aleasă pentru nimic nou, iar echipamentele din ea rămân."),
        canDelete,
        canHide: !canDelete && !l.is_hidden,
      });
      if (choice === "delete") {
        await this.change(() => api.deleteLocation(l.id), {
          done: `Sala „${locationText(l)}” a fost ștearsă`,
          fallback: "Eroare la ștergerea locației",
        });
      } else if (choice === "hide") {
        await this.toggleHidden(l);
      }
    },
  },
};
</script>

<style scoped>
.locs__tree-btns {
  margin: -6px 0 8px;
}
.locs__tree-btns .v-btn {
  color: var(--ink-2);
}
.locs__group + .locs__group {
  margin-top: 10px;
}
.locs__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--mint-50);
  font: inherit;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}
.locs__head:hover {
  border-color: var(--ink-3);
}
.locs__chevron.v-icon,
.locs__building.v-icon {
  color: var(--green);
}
.locs__block {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.1rem;
}
.locs__meta {
  color: var(--ink-3);
  font-size: 0.92rem;
}
.locs__list {
  margin-top: 6px;
}
.locs__floor {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 16px;
  background: #f7faf8;
  border-bottom: 1px solid var(--line-soft);
  color: var(--ink-2);
  font-weight: 700;
  font-size: 0.95rem;
}
.locs__floor .v-icon {
  color: var(--green);
}
.cfg-row.locs__row {
  align-items: center;
  padding-left: 28px;
}
@media (max-width: 600px) {
  .cfg-row.locs__row {
    padding-left: 14px;
  }
}
</style>
